import { ClientPreferences } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Plane, 
  HotelIcon, 
  AlertCircle,
  ExternalLink
} from "lucide-react";
import { InlineEditableField } from "@/components/InlineEditableField";

type CustomPreferencesRecord = Record<string, string>;

type StringRecord = Record<string, string>;

const toStringRecord = (input: unknown): StringRecord => {
  if (!input || Array.isArray(input) || typeof input !== "object") {
    return {};
  }

  return Object.entries(input as Record<string, unknown>).reduce<StringRecord>((acc, [key, value]) => {
    if (typeof key === "string") {
      acc[key] = value == null ? "" : String(value);
    }
    return acc;
  }, {});
};

const parseCustomPreferences = (
  raw: ClientPreferences["customPreferences"]
): CustomPreferencesRecord => toStringRecord(raw);

const buildUpdatedCustomPreferences = (
  record: CustomPreferencesRecord,
  key: string,
  value: string
): ClientPreferences["customPreferences"] => {
  const next: CustomPreferencesRecord = { ...record };
  const trimmed = value.trim();

  if (!trimmed) {
    delete next[key];
  } else {
    next[key] = trimmed;
  }

  return next;
};

const toNullableString = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
};

type PreferencesPanelProps = {
  preferences?: ClientPreferences;
  clientId: number;
  editMode?: boolean;
  onDraftChange?: (updates: Partial<ClientPreferences>) => void;
};

export function PreferencesPanel({ preferences, clientId, editMode = false, onDraftChange }: PreferencesPanelProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const preferencesId = preferences?.id ?? null;

  const customPreferences = preferences ? parseCustomPreferences(preferences.customPreferences) : {};
  const frequentFlyerNumbers = preferences ? toStringRecord(preferences.frequentFlyerNumbers) : {};
  const hotelLoyaltyPrograms = preferences ? toStringRecord(preferences.hotelLoyaltyPrograms) : {};
  const accessibilityRequirements = customPreferences.accessibilityRequirements ?? "";
  const additionalCustomPreferences = Object.entries(customPreferences).filter(
    ([key]) => key !== "accessibilityRequirements"
  );

  const addPrefs = useMutation({
    mutationFn: async () => apiRequest('POST', '/api/client-preferences', { clientId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/preferences`] });
      toast({ title: 'Preferences added' });
    },
    onError: () => toast({ title: 'Failed to add preferences', variant: 'destructive' })
  });

  const patch = async (id: number, updates: Partial<ClientPreferences>) => {
    await apiRequest('PATCH', `/api/client-preferences/${id}`, updates);
    await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/preferences`] });
  };

  const savePreferencesField = useCallback(
    async (updates: Partial<ClientPreferences>) => {
      if (preferencesId == null) {
        return;
      }
      await patch(preferencesId, updates);
    },
    [preferencesId, patch],
  );

  return (
    <div className="space-y-6">
      {!preferences ? (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              No travel preferences have been set for this contact yet.
            </AlertDescription>
          </Alert>
          
          <Button onClick={() => setIsEditDialogOpen(true)}>
            Add Travel Preferences
          </Button>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-medium">Travel Preferences</h3>
            <Button variant="outline" size="sm" onClick={() => setIsEditDialogOpen(true)}>
              Edit Preferences
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Plane className="h-4 w-4" />
                  <span>Flight Preferences</span>
                </h4>
                
                <dl className="space-y-4">
                  <InlineEditableField
                    label="Seat Preference"
                    value={preferences.seatPreference}
                    placeholder="Aisle/Window/etc."
                    forceEdit={false}
                    onSave={async (val) =>
                      savePreferencesField({ seatPreference: toNullableString(val) })
                    }
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ seatPreference: toNullableString(val) })}
                  />
                  <InlineEditableField
                    label="Meal Preference"
                    value={preferences.mealPreference}
                    placeholder="Vegan/Gluten-free/etc."
                    forceEdit={false}
                    onSave={async (val) =>
                      savePreferencesField({ mealPreference: toNullableString(val) })
                    }
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ mealPreference: toNullableString(val) })}
                  />
                  
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Frequent Flyer Programs</dt>
                    <dd>
                      {Object.keys(frequentFlyerNumbers).length > 0 ? (
                        <ul className="list-disc list-inside">
                          {Object.entries(frequentFlyerNumbers).map(([airline, number]) => (
                            <li key={airline} className="text-sm">
                              {airline}: {number}
                            </li>
                          ))}
                        </ul>
                      ) : "—"}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <HotelIcon className="h-4 w-4" />
                  <span>Hotel Preferences</span>
                </h4>
                
                <dl className="space-y-4">
                  <InlineEditableField
                    label="Room Preference"
                    value={preferences.roomPreference}
                    placeholder="King/Double, High floor, etc."
                    forceEdit={false}
                    onSave={async (val) =>
                      savePreferencesField({ roomPreference: toNullableString(val) })
                    }
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ roomPreference: toNullableString(val) })}
                  />
                  
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Hotel Loyalty Programs</dt>
                    <dd>
                      {Object.keys(hotelLoyaltyPrograms).length > 0 ? (
                        <ul className="list-disc list-inside">
                          {Object.entries(hotelLoyaltyPrograms).map(([hotel, number]) => (
                            <li key={hotel} className="text-sm">
                              {hotel}: {number}
                            </li>
                          ))}
                        </ul>
                      ) : "—"}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span>Special Requests & Other Preferences</span>
                </h4>
                
                <dl className="space-y-4">
                  <InlineEditableField
                    label="Special Requests"
                    value={preferences.specialRequests}
                    placeholder="Requests"
                    forceEdit={false}
                    onSave={async (val) =>
                      savePreferencesField({ specialRequests: toNullableString(val) })
                    }
                    bulkEdit={editMode}
                    onChange={(val) =>
                      onDraftChange?.({ specialRequests: toNullableString(val) })
                    }
                  />
                  <InlineEditableField
                    label="Accessibility Requirements"
                    value={accessibilityRequirements}
                    placeholder="Accessibility"
                    forceEdit={false}
                    onSave={async (val) =>
                      savePreferencesField({
                        customPreferences: buildUpdatedCustomPreferences(customPreferences, "accessibilityRequirements", val ?? ""),
                      })
                    }
                    bulkEdit={editMode}
                    onChange={(val) =>
                      onDraftChange?.({
                        customPreferences: buildUpdatedCustomPreferences(customPreferences, "accessibilityRequirements", val ?? ""),
                      })
                    }
                  />
                  <InlineEditableField
                    label="Car Rental Preferences"
                    value={preferences.rentalCarPreferences}
                    placeholder="Vendor, car class, etc."
                    forceEdit={!editMode}
                    onSave={async (val) =>
                      savePreferencesField({ rentalCarPreferences: toNullableString(val) })
                    }
                    bulkEdit={editMode}
                    onChange={(val) =>
                      onDraftChange?.({ rentalCarPreferences: toNullableString(val) })
                    }
                  />
                  
                  {additionalCustomPreferences.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Other Preferences</dt>
                      <dd>
                        <ul className="list-disc list-inside">
                          {additionalCustomPreferences.map(([key, value]) => (
                            <li key={key} className="text-sm">
                              {key}: {value}
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Create/Edit Dialog - To be implemented */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {preferences ? "Edit Travel Preferences" : "Add Travel Preferences"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to allow adding/editing travel preferences.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
